import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Search,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  Eye,
  X,
  Play
} from 'lucide-react';
import apiService from './services/api';
import { Button, LoadingSpinner, Badge, Panel } from './components';

// Status badge mapping
const getStatusBadge = (status) => {
  const statusMap = {
    'SUCCESS': { variant: 'success', label: 'Success' },
    'FAILURE': { variant: 'error', label: 'Failed' },
    'PENDING': { variant: 'warning', label: 'Pending' },
    'STARTED': { variant: 'info', label: 'Running' },
    'RETRY': { variant: 'warning', label: 'Retry' },
    'REVOKED': { variant: 'neutral', label: 'Revoked' },
  };
  return statusMap[status] || { variant: 'neutral', label: status };
};

// Format duration
const formatDuration = (seconds) => {
  if (!seconds) return '-';
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

// Task name display (strip module path)
const formatTaskName = (taskName) => {
  if (!taskName) return 'Unknown';
  return taskName.split('.').pop();
};

// Job Detail Modal Component
function JobDetailModal({ job, onClose }) {
  const statusInfo = getStatusBadge(job.status);

  // Pretty print JSON
  const formatJSON = (data) => {
    if (!data) return null;
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return String(data);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{formatTaskName(job.task_name)}</h2>
            <p className="text-xs text-gray-500 font-mono">{job.task_id}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Status and Timing */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Status</p>
              <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Duration</p>
              <p className="text-sm font-medium">{formatDuration(job.duration_seconds)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Started</p>
              <p className="text-sm">{formatDate(job.date_created)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Completed</p>
              <p className="text-sm">{formatDate(job.date_done)}</p>
            </div>
          </div>

          {/* Arguments */}
          {(job.task_args || job.task_kwargs) && (
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-1">Arguments</p>
              <pre className="bg-gray-50 p-2 rounded text-xs overflow-x-auto">
                {job.task_args && <div>args: {job.task_args}</div>}
                {job.task_kwargs && <div>kwargs: {job.task_kwargs}</div>}
              </pre>
            </div>
          )}

          {/* Result */}
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-1">Result</p>
            <pre className="bg-gray-50 p-2 rounded text-xs overflow-x-auto max-h-48">
              {formatJSON(job.result) || 'No result'}
            </pre>
          </div>

          {/* Traceback (if failed) */}
          {job.traceback && (
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-1">Error Traceback</p>
              <pre className="bg-red-50 text-red-700 p-2 rounded text-xs overflow-x-auto max-h-64 whitespace-pre-wrap">
                {job.traceback}
              </pre>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200">
          <Button onClick={onClose} variant="secondary" size="sm">Close</Button>
        </div>
      </div>
    </div>
  );
}

function JobsPage() {
  const [activeTab, setActiveTab] = useState('history');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [taskFilter, setTaskFilter] = useState('all');
  const [sortField, setSortField] = useState('date_done');
  const [sortDirection, setSortDirection] = useState('desc');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const itemsPerPage = 20;

  // API state
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [scheduledTasks, setScheduledTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Detail modal state
  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Run task state
  const [runningTask, setRunningTask] = useState(null);
  const [taskResult, setTaskResult] = useState(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [jobsData, statsData, scheduledData] = await Promise.all([
        apiService.getJobs({ limit: 500 }),
        apiService.getJobStats(),
        apiService.getScheduledTasks(),
      ]);

      setJobs(Array.isArray(jobsData?.results) ? jobsData.results : (Array.isArray(jobsData) ? jobsData : []));
      setStats(statsData);
      setScheduledTasks(Array.isArray(scheduledData) ? scheduledData : []);
    } catch (err) {
      setError(err.message || 'Failed to load job data');
      console.error('Error loading job data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load and auto-refresh
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchData, 10000); // 10 seconds
    }
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  // Get unique task names for filter
  const uniqueTaskNames = useMemo(() => {
    const names = [...new Set(jobs.map(job => job.task_name).filter(Boolean))];
    return names.sort();
  }, [jobs]);

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = [...jobs];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(job =>
        (job.task_id && job.task_id.toLowerCase().includes(searchLower)) ||
        (job.task_name && job.task_name.toLowerCase().includes(searchLower)) ||
        (job.result && String(job.result).toLowerCase().includes(searchLower))
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(job => job.status === statusFilter);
    }

    // Apply task filter
    if (taskFilter !== 'all') {
      filtered = filtered.filter(job => job.task_name === taskFilter);
    }

    // Sort data
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'date_done' || sortField === 'date_created') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [jobs, searchTerm, statusFilter, taskFilter, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const paginatedData = filteredAndSortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Handle view details
  const handleViewDetails = async (job) => {
    try {
      const detail = await apiService.getJob(job.id);
      setSelectedJob(detail);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Error fetching job details:', err);
    }
  };

  // Handle run task
  const handleRunTask = async (taskName) => {
    setRunningTask(taskName);
    setTaskResult(null);
    try {
      const result = await apiService.runTask(taskName);
      setTaskResult({
        success: true,
        message: result.message || `Task '${taskName.split('.').pop()}' has been queued`,
        taskId: result.task_id,
      });
      // Refresh data after a short delay to show the new task
      setTimeout(fetchData, 2000);
    } catch (err) {
      setTaskResult({
        success: false,
        message: err.message || 'Failed to run task',
      });
    } finally {
      setRunningTask(null);
    }
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="flex-1 bg-neutral-50 p-3 overflow-y-auto">
        <div className="h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-neutral-50 p-3 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      {/* Header */}
      <div className="mb-3 flex justify-between items-center">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 mb-1">Background Jobs</h1>
          <p className="text-xs text-gray-600">Monitor Celery task execution and scheduled jobs</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300"
            />
            Auto-refresh
          </label>
          <Button onClick={fetchData} variant="secondary" size="sm">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
          <div className="bg-white rounded-lg p-2 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Total Tasks (7d)</p>
                <p className="text-lg font-semibold text-gray-800">{stats.total || 0}</p>
              </div>
              <Clock className="w-3.5 h-3.5 text-gray-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-2 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Success (24h)</p>
                <p className="text-lg font-semibold text-green-600">{stats.last_24h?.SUCCESS || 0}</p>
              </div>
              <CheckCircle className="w-3.5 h-3.5 text-green-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-2 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Failed (24h)</p>
                <p className="text-lg font-semibold text-red-600">{stats.last_24h?.FAILURE || 0}</p>
              </div>
              <XCircle className="w-3.5 h-3.5 text-red-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-2 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Pending</p>
                <p className="text-lg font-semibold text-yellow-600">{stats.by_status?.PENDING || 0}</p>
              </div>
              <AlertCircle className="w-3.5 h-3.5 text-yellow-600" />
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-3">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('history')}
            className={`flex items-center px-3 py-2 text-xs font-medium transition-colors ${
              activeTab === 'history'
                ? 'border-b-2 border-red-800 text-red-800'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Clock className="w-3.5 h-3.5 mr-2" />
            Task History
          </button>
          <button
            onClick={() => setActiveTab('scheduled')}
            className={`flex items-center px-3 py-2 text-xs font-medium transition-colors ${
              activeTab === 'scheduled'
                ? 'border-b-2 border-red-800 text-red-800'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Calendar className="w-3.5 h-3.5 mr-2" />
            Scheduled Tasks
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-3">
          <p>{error}</p>
        </div>
      )}

      {/* Task Run Result Notification */}
      {taskResult && (
        <div className={`mb-3 p-3 rounded-lg flex items-center justify-between ${
          taskResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center gap-2">
            {taskResult.success ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <XCircle className="w-4 h-4 text-red-600" />
            )}
            <span className={`text-sm ${taskResult.success ? 'text-green-800' : 'text-red-800'}`}>
              {taskResult.message}
            </span>
          </div>
          <button
            onClick={() => setTaskResult(null)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {activeTab === 'history' && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-lg p-2 shadow-sm mb-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                <input
                  type="text"
                  placeholder="Search task ID or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-2 py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800 focus:border-transparent"
                />
              </div>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-2 py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              >
                <option value="all">All Status</option>
                <option value="SUCCESS">Success</option>
                <option value="FAILURE">Failed</option>
                <option value="PENDING">Pending</option>
                <option value="STARTED">Running</option>
                <option value="RETRY">Retry</option>
              </select>

              {/* Task Filter */}
              <select
                value={taskFilter}
                onChange={(e) => setTaskFilter(e.target.value)}
                className="w-full px-2 py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              >
                <option value="all">All Tasks</option>
                {uniqueTaskNames.map(name => (
                  <option key={name} value={name}>{formatTaskName(name)}</option>
                ))}
              </select>

              {/* Results count */}
              <div className="flex items-center text-xs text-gray-600">
                Showing {filteredAndSortedData.length} results
              </div>
            </div>
          </div>

          {/* Task History Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('task_name')}
                    >
                      <div className="flex items-center">
                        Task
                        {sortField === 'task_name' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />
                        )}
                      </div>
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">Status</th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('date_done')}
                    >
                      <div className="flex items-center">
                        Completed
                        {sortField === 'date_done' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />
                        )}
                      </div>
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">Duration</th>
                    <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">Result</th>
                    <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-2 py-12 text-center text-gray-500">
                        <div className="flex flex-col items-center">
                          <Clock className="w-12 h-12 mb-3 text-gray-300" />
                          <p className="text-xs">No task results found</p>
                          <p className="text-xs text-gray-400 mt-1">Tasks will appear here after they run</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map(job => {
                      const statusInfo = getStatusBadge(job.status);
                      return (
                        <tr key={job.id} className="hover:bg-neutral-50">
                          <td className="px-2 py-1.5">
                            <div>
                              <p className="text-xs font-medium text-gray-800">{formatTaskName(job.task_name)}</p>
                              <p className="text-xs text-gray-400 font-mono truncate max-w-xs">{job.task_id?.slice(0, 8)}...</p>
                            </div>
                          </td>
                          <td className="px-2 py-1.5">
                            <Badge variant={statusInfo.variant} small>{statusInfo.label}</Badge>
                          </td>
                          <td className="px-2 py-1.5 text-xs text-gray-600">{formatDate(job.date_done)}</td>
                          <td className="px-2 py-1.5 text-xs text-gray-600">{formatDuration(job.duration_seconds)}</td>
                          <td className="px-2 py-1.5 text-xs text-gray-600">
                            <span className="truncate block max-w-xs" title={job.result_preview}>
                              {job.result_preview || '-'}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <button
                              onClick={() => handleViewDetails(job)}
                              className="text-red-800 hover:text-red-900"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-2 py-2 border-t border-gray-200 flex items-center justify-between">
                <div className="text-xs text-gray-600">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedData.length)} of {filteredAndSortedData.length} tasks
                </div>
                <div className="flex gap-1">
                  <Button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    variant={currentPage === 1 ? 'secondary' : 'primary'}
                    size="sm"
                  >
                    Previous
                  </Button>
                  <Button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    variant={currentPage === totalPages ? 'secondary' : 'primary'}
                    size="sm"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'scheduled' && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">Task Name</th>
                  <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">Schedule</th>
                  <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">Status</th>
                  <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">Last Run</th>
                  <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">Last Status</th>
                  <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {scheduledTasks.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-2 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <Calendar className="w-12 h-12 mb-3 text-gray-300" />
                        <p className="text-xs">No scheduled tasks found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  scheduledTasks.map((task, index) => {
                    const lastStatusInfo = task.last_status ? getStatusBadge(task.last_status) : null;
                    return (
                      <tr key={index} className="hover:bg-neutral-50">
                        <td className="px-2 py-1.5">
                          <div>
                            <p className="text-xs font-medium text-gray-800">{task.name}</p>
                            <p className="text-xs text-gray-400">{formatTaskName(task.task)}</p>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-600">{task.schedule}</td>
                        <td className="px-2 py-1.5">
                          <Badge variant={task.enabled ? 'success' : 'neutral'} small>
                            {task.enabled ? 'Active' : 'Disabled'}
                          </Badge>
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-600">
                          {task.last_run ? formatDate(task.last_run) : 'Never'}
                        </td>
                        <td className="px-2 py-1.5">
                          {lastStatusInfo ? (
                            <Badge variant={lastStatusInfo.variant} small>{lastStatusInfo.label}</Badge>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <button
                            onClick={() => handleRunTask(task.task)}
                            disabled={runningTask === task.task}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Run this task now"
                          >
                            {runningTask === task.task ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Play className="w-3 h-3" />
                            )}
                            Run Now
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedJob && (
        <JobDetailModal
          job={selectedJob}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedJob(null);
          }}
        />
      )}
    </div>
  );
}

export default JobsPage;
